import { useEffect, useState } from 'react';
import flagsmithService, {
  getFeatureValue,
  hasFeature,
  initFlagsmith,
} from '../lib/flagsmith';

/**
 * Hook to initialize Flagsmith and check feature flags
 *
 * @param featureId Optional feature ID to check
 * @param defaultValue Optional default value to use if the feature is not found
 * @returns Object with loading state, feature enabled state, and feature value
 */
export function useFeatureFlag<T>(featureId?: string, defaultValue?: T) {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [value, setValue] = useState<T | undefined>(defaultValue);

  // Initialize Flagsmith on mount
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        await initFlagsmith();

        if (isMounted) {
          setInitialized(true);
          setLoading(false);

          // If a feature ID was provided, check its status
          if (featureId) {
            setEnabled(hasFeature(featureId));
            try {
              setValue(getFeatureValue<T>(featureId, defaultValue));
            } catch (error) {
              console.warn(`Feature value not found for ${featureId}`, error);
              setValue(defaultValue);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize Flagsmith:', error);
        if (isMounted) {
          setLoading(false);
          // Use default value if initialization fails
          if (featureId && defaultValue !== undefined) {
            setValue(defaultValue);
          }
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [featureId, defaultValue]);

  return {
    loading,
    initialized,
    enabled,
    value,
    // Expose the raw service for advanced usage
    flagsmith: flagsmithService,
  };
}

export default useFeatureFlag;
